// LogoFieldView.jsx — View PURA do CAMPO LOGO do editor de página (identidade da página).
// Recebe { vm, actions }. Contrato: logoField.vm.js (@version 1). Rótulos, aviso e o tipo (library/legacy/none)
// vêm prontos. `useState` só de UI: a miniatura que falhou ao carregar.
//
// DESENHO: o logo aparece no lugar onde vai morar — uma pequena "cabeça de página" (faixa + logo centrado),
// não um campo de arquivo. Tocar no logo (ou no botão) abre a biblioteca em modo 'logo'. Sem logo, o lugar
// fica tracejado com o convite; logo antigo, o aviso convida a escolher de novo. Container query: lado a lado
// a partir de ~420 px da coluna, empilhado no celular (~300 px) — nada estoura.
// O formato do recorte (redondo, quadrado…) é outro campo: aqui a marca aparece inteira (contain).
import { useState } from 'react';
import { Box, Typography, Button, ButtonBase } from '@mui/material';
import PhotoLibraryRounded from '@mui/icons-material/PhotoLibraryRounded';
import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';

const HEAD = { fontWeight: 'var(--fw-bold)' };
const FOCUS = { outline: '2px solid var(--color-primary)', outlineOffset: 2 };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const BRAND_OUTLINE_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none', whiteSpace: 'nowrap',
  border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
  fontWeight: 'var(--fw-bold)', bgcolor: 'var(--surface)',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': FOCUS,
};

const QUIET_BTN_SX = {
  minHeight: 42, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': FOCUS,
};

export default function LogoFieldView({ vm, actions }) {
  const a = actions || {};
  const [brokenUrl, setBrokenUrl] = useState(null); // UI: miniatura que falhou
  if (!vm) return null;

  const none = vm.kind === 'none' || !vm.thumbnailUrl;
  const broken = !none && brokenUrl === vm.thumbnailUrl;
  const legacy = vm.kind === 'legacy';

  return (
    <Box sx={{ containerType: 'inline-size', minWidth: 0, color: 'var(--text-primary)' }}>
      <Typography variant="subtitle2" component="p" id="logo-field-label" sx={{ ...HEAD, color: 'var(--text-primary)' }}>
        {vm.label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', lineHeight: 1.45, mt: '2px', mb: 1.25 }}>
        {vm.helper}
      </Typography>

      <Box
        role="group"
        aria-labelledby="logo-field-label"
        sx={{
          border: '1px solid', borderColor: legacy ? 'var(--tint-info-border)' : 'var(--divider)',
          borderRadius: 'var(--radius-card)', bgcolor: 'var(--surface)', overflow: 'hidden',
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 420px)': { gridTemplateColumns: 'minmax(200px, 260px) minmax(0, 1fr)' },
        }}
      >
        {/* a "cabeça da página": onde o logo vai aparecer */}
        <Box
          sx={{
            position: 'relative', bgcolor: 'var(--bg-page)', minHeight: 132,
            display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
            borderBottom: '1px solid', borderColor: 'var(--divider)',
            '@container (min-width: 420px)': { borderBottom: 0, borderRight: '1px solid var(--divider)' },
          }}
        >
          <Box aria-hidden="true" sx={{ position: 'absolute', top: 10, left: 12, right: 12, display: 'flex', gap: '4px' }}>
            {[0, 1, 2].map((i) => <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'var(--divider)' }} />)}
          </Box>
          <ButtonBase
            focusRipple
            onClick={() => a.onChoose?.()}
            aria-label={vm.chooseLabel}
            sx={{
              position: 'relative', width: 96, height: 96, borderRadius: 'var(--radius-card)', overflow: 'hidden',
              bgcolor: 'var(--surface)',
              border: none ? '2px dashed' : '1px solid',
              borderColor: none ? 'var(--color-primary-light)' : 'var(--divider)',
              boxShadow: none ? 'none' : 'var(--shadow-card)',
              transition: 'box-shadow .15s, border-color .15s',
              '&:hover': { borderColor: 'var(--color-primary)', boxShadow: none ? 'none' : 'var(--shadow-hover)' },
              '&:hover .logoOverlay, &.Mui-focusVisible .logoOverlay': { opacity: 1 },
              '&.Mui-focusVisible': { outline: '3px solid var(--color-primary-dark)', outlineOffset: 3 },
            }}
          >
            {none ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: 'var(--color-primary-dark)' }}>
                <AddPhotoAlternateRounded sx={{ fontSize: 30 }} />
              </Box>
            ) : broken ? (
              <BrokenImageRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />
            ) : (
              <Box
                component="img"
                src={vm.thumbnailUrl}
                alt={vm.label}
                onError={() => setBrokenUrl(vm.thumbnailUrl)}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            )}
            {!none && (
              <Box className="logoOverlay" aria-hidden="true" sx={{ position: 'absolute', inset: 0, opacity: 0, transition: 'opacity .15s', bgcolor: 'color-mix(in srgb, var(--surface) 70%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PhotoLibraryRounded sx={{ fontSize: 26, color: 'var(--color-primary-dark)' }} />
              </Box>
            )}
          </ButtonBase>
        </Box>

        {/* ações + aviso */}
        <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.25, minWidth: 0 }}>
          {legacy && vm.noticeLabel && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)', borderRadius: 'var(--radius-sm)', px: 1.25, py: 1 }}>
              <HistoryRounded sx={{ fontSize: 18, color: 'var(--tint-info-fg)', flexShrink: 0, mt: '1px' }} />
              <Typography variant="caption" sx={{ color: 'var(--tint-info-fg)', lineHeight: 1.45 }}>{vm.noticeLabel}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Button
              onClick={() => a.onChoose?.()}
              startIcon={none ? <AddPhotoAlternateRounded /> : <PhotoLibraryRounded />}
              disableElevation
              sx={none || legacy ? PRIMARY_BTN_SX : BRAND_OUTLINE_BTN_SX}
            >
              {vm.chooseLabel}
            </Button>
            {vm.removeLabel && (
              <Button onClick={() => a.onRemove?.()} startIcon={<RemoveCircleOutlineRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>
                {vm.removeLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
